import { useQuery, type ApolloError } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import type { SupervisorFormData, SupervisorNominationData } from './types';
import type {
  DeclarationValues,
  FundingsValue,
  FundingDeclaration,
  FundingPlan,
  FundingSource,
} from '../../../Nomination/components';
import { NominationDeclaration } from '../../../Nomination/graphql';
import { checkInvalidDate } from '../../../Application/graphql';

const GET_SUPERVISOR_NOMINATION = gql`
  query ($id: ID!) {
    nomination(id: $id) {
      supervisors {
        type
        title
        jobTitle
        givenName
        surname
        department
        institution
        email
        previousSuccessfulSupervision
        isDrcChair
        isDoRPgS
        confirmedAsCoSupervisor
        coSupervisorDeclarationRemark
      }

      fundings {
        schema
        source
        sufficientFundingConfirm
        fundingPlan {
          accountCode
          period {
            from
            to
          }
        }
        requiredDocuments {
          id
          name
          link
        }
        proofOfScheme {
          id
          name
          link
        }
        projectInformation {
          role
          PIName
          PIDepartment
          declarations {
            content
            confirmed
            accountCode
          }
          justification
        }
      }

      admission {
        programme
        requirements {
          generalEntranceRequirements {
            requirements
            justification
          }
          englishLanguageRequirements {
            requirements
          }
          justification
        }
      }

      declarations {
        content
        confirmed
        justification
      }

      remarks
    }
  }
`;

type GetSupervisorNomination = {
  supervisorNomination: SupervisorNominationData;
  loading: boolean;
  error: ApolloError | undefined;
};

export const useGetSupervisorNomination = (
  id: string,
): GetSupervisorNomination => {
  const {
    data: supervisorNominationData,
    loading,
    error,
  } = useQuery(GET_SUPERVISOR_NOMINATION, {
    variables: { id },
    skip: !id,
  });

  let {
    supervisors,
    admission,
    fundings: rawFundings,
    declarations = [],
    remarks,
  } = supervisorNominationData?.nomination ?? {};

  if (admission !== undefined && admission !== null) {
    const {
      programme,
      requirements: {
        generalEntranceRequirements: [
          {
            requirements: entranceRequirement,
            justification: entranceJustification,
          },
        ],
        englishLanguageRequirements: [{ requirements: languageRequirement }],
        justification,
      },
    } = admission ?? {};

    admission = {
      programme,
      entranceRequirement,
      entranceJustification,
      languageRequirement,
      justification,
    };
  }

  const fundings: FundingsValue[] =
    (rawFundings ?? [])?.map((fund: any) => {
      const transformedSources = (fund?.source as FundingSource) ?? '';
      const transformedFundingPlan: FundingPlan[] = (
        fund?.fundingPlan ?? []
      ).map((plan: FundingPlan) => ({
        ...plan,
        period: {
          from: checkInvalidDate(plan.period.from) ? null : plan.period.from,
          to: checkInvalidDate(plan.period.to) ? null : plan.period.to,
        },
      }));
      const transformedProjectInformation = fund?.projectInformation ?? {};

      return {
        schema: fund?.schema ?? '',
        sufficientFundingConfirm: fund?.sufficientFundingConfirm ?? false,
        source: transformedSources,
        fundingPlan: transformedFundingPlan,
        requiredDocuments: fund?.requiredDocuments ?? [],
        projectInformation: transformedProjectInformation,
        proofOfScheme: fund?.proofOfScheme ?? [],
        declaration: (fund?.declaration as FundingDeclaration[]) ?? [],
      };
    }) ?? [];

  const emptyDeclarationData: DeclarationValues[] = [
    {
      content: NominationDeclaration.NO_INTEREST_CONFLICT,
      confirmed: false,
      justification: '',
    },
    {
      content: NominationDeclaration.NOT_REGISTER_HIGHER_DEGREE,
      confirmed: false,
    },
    {
      content: NominationDeclaration.INFORMATION_TRUE,
      confirmed: false,
    },
  ];

  const supervisorNomination: SupervisorNominationData = {
    supervisors: (supervisors ?? []) as SupervisorFormData[],
    admission,
    fundings,
    declarations: (declarations?.length as boolean)
      ? emptyDeclarationData
      : (declarations as DeclarationValues[]),
    remarks: remarks ?? [''],
  };

  return {
    supervisorNomination,
    loading,
    error,
  };
};
